import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { api, setCachedUser, setToken } from "../api";
import type { ForceWww, PanelUser, Site, SiteType } from "../types";

/**
 * Premium Domain List + Add Domain (redesign_part2.md).
 */
export function SitesPage({
  sites,
  accounts,
  phpVersions,
  gatewayPort,
  currentUser,
  initialMode = "list",
  onCreate,
  onEnable,
  onDisable: _onDisable,
  onDelete,
  onSetPhp: _onSetPhp,
  onIssueSsl,
  onForceHttps: _onForceHttps,
  onCustomSsl: _onCustomSsl,
  onRenewSsl,
  onRefresh
}: {
  sites: Site[];
  accounts: PanelUser[];
  phpVersions: { version: string; available: boolean; default?: boolean }[];
  gatewayPort: number;
  currentUser: PanelUser;
  initialMode?: "list" | "add";
  onCreate: (data: {
    userId: string;
    domain: string;
    type: SiteType;
    documentRoot: string;
    phpVersion: string;
    forceWww: ForceWww;
  }) => Promise<Site | void>;
  onEnable: (id: string) => Promise<void>;
  onDisable: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetPhp: (id: string, phpVersion: string) => Promise<void>;
  onIssueSsl: (id: string) => Promise<string | void>;
  onForceHttps: (id: string, force: boolean) => Promise<void>;
  onCustomSsl: (
    id: string,
    data: { certificate: string; privateKey: string; chain?: string }
  ) => Promise<void>;
  onRenewSsl: (dryRun?: boolean) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  void _onDisable;
  void _onSetPhp;
  void _onForceHttps;
  void _onCustomSsl;

  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const endUsers = useMemo(
    () =>
      accounts.filter((a) => a.role === "user" && a.provisionStatus === "ready"),
    [accounts]
  );
  const isEndUser = !staff && currentUser.role === "user";

  const accountById = useMemo(() => {
    const m = new Map<string, PanelUser>();
    for (const a of accounts) m.set(a.id, a);
    return m;
  }, [accounts]);

  const [mode, setMode] = useState<"list" | "add">(initialMode);
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const [domain, setDomain] = useState("");
  const [userId, setUserId] = useState(endUsers[0]?.id || currentUser.id);
  const [type, setType] = useState<SiteType>("addon");
  const [documentRoot, setDocumentRoot] = useState("");
  const [autoPath, setAutoPath] = useState(true);
  const [phpVersion, setPhpVersion] = useState(
    phpVersions.find((p) => p.default)?.version || "8.3"
  );
  const [forceWww, setForceWww] = useState<ForceWww>("none");
  const [issueSsl, setIssueSsl] = useState(true);
  const [allowWildcard, setAllowWildcard] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [primaryIp, setPrimaryIp] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sortKey, setSortKey] = useState<"domain" | "user" | "type" | "path">(
    "domain"
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const homePrefix = useMemo(() => {
    const acct = staff
      ? endUsers.find((a) => a.id === userId)
      : currentUser;
    const home = acct?.homePath || `/home/${acct?.username || "user"}`;
    return home.endsWith("/") ? home : `${home}/`;
  }, [staff, endUsers, userId, currentUser]);

  const selectedAccount = useMemo(
    () => accountById.get(userId) || endUsers.find((a) => a.id === userId),
    [accountById, endUsers, userId]
  );

  const domainCountForUser = useMemo(
    () => sites.filter((s) => s.userId === userId).length,
    [sites, userId]
  );

  useEffect(() => {
    if (!staff && currentUser.id) setUserId(currentUser.id);
  }, [staff, currentUser.id]);

  useEffect(() => {
    if (autoPath && domain.trim()) {
      const d = domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/\/.*$/, "");
      if (type === "addon" || type === "subdomain") {
        setDocumentRoot(`public_html/${d}`);
      } else if (type === "primary") {
        setDocumentRoot("public_html");
      } else {
        setDocumentRoot(`public_html/${d}`);
      }
    }
  }, [domain, type, autoPath]);

  useEffect(() => {
    let cancelled = false;
    void api<{ config?: { primaryIp?: string } }>("/api/v1/server-config/basic")
      .then((r) => {
        if (!cancelled) setPrimaryIp(r.config?.primaryIp || "");
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const pathDisplay = useCallback(
    (s: Site) => {
      if (s.absoluteRoot) return s.absoluteRoot;
      const u = accountById.get(s.userId);
      const home = u?.homePath || `/home/${s.username || "user"}`;
      const root = s.documentRoot.replace(/^\//, "");
      return `${home.replace(/\/$/, "")}/${root}`;
    },
    [accountById]
  );

  const ownerOf = useCallback(
    (s: Site) => {
      const u = accountById.get(s.userId);
      return u?.ownerUsername || "root";
    },
    [accountById]
  );

  const domainValidation = useMemo(() => {
    const d = domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/\/.*$/, "");
    if (!d) return { ok: false as const, msg: "" };
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(d) && type !== "subdomain") {
      if (!/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i.test(d)) {
        return { ok: false as const, msg: "Invalid domain" };
      }
    }
    if (sites.some((s) => s.domain.toLowerCase() === d)) {
      return { ok: false as const, msg: "Domain already exists" };
    }
    return { ok: true as const, msg: "Valid domain" };
  }, [domain, sites, type]);

  const filtered = useMemo(() => {
    let list = sites;
    if (typeFilter !== "all") {
      list = list.filter((x) => x.type === typeFilter);
    }
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((x) => {
        const path = pathDisplay(x).toLowerCase();
        const owner = ownerOf(x).toLowerCase();
        return (
          x.domain.toLowerCase().includes(s) ||
          (x.username || "").toLowerCase().includes(s) ||
          path.includes(s) ||
          x.type.includes(s) ||
          owner.includes(s)
        );
      });
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let av = "";
      let bv = "";
      if (sortKey === "domain") {
        av = a.domain;
        bv = b.domain;
      } else if (sortKey === "user") {
        av = a.username || "";
        bv = b.username || "";
      } else if (sortKey === "type") {
        av = a.type;
        bv = b.type;
      } else {
        av = pathDisplay(a);
        bv = pathDisplay(b);
      }
      return av.localeCompare(bv) * dir;
    });
  }, [sites, q, typeFilter, sortKey, sortDir, pathDisplay, ownerOf]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (safePage - 1) * perPage,
    safePage * perPage
  );
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const selectableOnPage = pageItems.filter((s) => s.type !== "primary");
  const allPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((s) => selected[s.id]);

  const counts = useMemo(() => {
    const primary = sites.filter((s) => s.type === "primary").length;
    const addon = sites.filter((s) => s.type === "addon").length;
    const sub = sites.filter((s) => s.type === "subdomain").length;
    return { primary, addon, sub, total: sites.length };
  }, [sites]);

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!domainValidation.ok) {
      setError(domainValidation.msg || "Invalid domain");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const d = domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/\/.*$/, "");
      const rootPart = documentRoot.trim().replace(/^\/+/, "");
      const docRoot = rootPart || d;

      const site = await onCreate({
        userId: staff ? userId : currentUser.id,
        domain: d,
        type,
        documentRoot: docRoot,
        phpVersion,
        forceWww
      });

      if (issueSsl && site?.id) {
        try {
          const note = await onIssueSsl(site.id);
          setMsg(
            note
              ? `Domain added. SSL: ${note}`
              : `Domain ${d} added and SSL issued.`
          );
        } catch (sslErr) {
          setMsg(
            `Domain ${d} added, but SSL failed: ${
              sslErr instanceof Error ? sslErr.message : String(sslErr)
            }`
          );
        }
      } else {
        setMsg(`Domain ${d} added successfully.`);
      }

      setDomain("");
      setDocumentRoot("");
      setAllowWildcard(false);
      setAutoPath(true);
      setMode("list");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    if (sites.some((s) => selectedIds.includes(s.id) && s.type === "primary")) {
      setError("Primary domains cannot be bulk-deleted.");
      return;
    }
    if (!confirm(`Delete ${selectedIds.length} domain(s)?`)) return;
    setBusy(true);
    setError("");
    try {
      await Promise.all(selectedIds.map((id) => onDelete(id)));
      setSelected({});
      setMsg("Selected domains deleted.");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (s: Site) => {
    if (s.type === "primary") {
      setError("Primary domains cannot be deleted from this list.");
      return;
    }
    if (!confirm(`Delete domain ${s.domain}?`)) return;
    setBusy(true);
    setError("");
    try {
      await onDelete(s.id);
      setMsg(`Deleted ${s.domain}`);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["#", "Domain", "User", "Owner", "IP", "Path", "Type", "PHP", "Status"],
      ...filtered.map((s, i) => [
        String(i + 1),
        s.domain,
        s.username || "",
        ownerOf(s),
        primaryIp,
        pathDisplay(s),
        s.type,
        s.phpVersion,
        s.status
      ])
    ];
    const esc = (c: string) =>
      /["\n,]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `domains-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loginAsUser = async (s: Site) => {
    if (!staff || !s.userId) {
      window.open(
        s.sslEnabled ? `https://${s.domain}` : `http://${s.domain}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api<{
        ok: boolean;
        token?: string;
        user?: PanelUser;
      }>("/api/v1/auth/impersonate", {
        method: "POST",
        body: JSON.stringify({ userId: s.userId })
      });
      if (res.token && res.user) {
        setToken(res.token);
        setCachedUser(res.user);
        window.location.reload();
        return;
      }
      throw new Error("Impersonation failed");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const typeLabel = (t: SiteType) => {
    if (t === "primary") return "Primary";
    if (t === "addon") return "Addon";
    if (t === "subdomain") return "Subdomain";
    return "Alias";
  };

  const typeHelp: Record<string, string> = {
    primary: "Main website for this hosting account.",
    addon: "Creates a separate website directory under this hosting account.",
    subdomain: "Creates a subdomain pointing to a folder under the account.",
    alias: "Parked domain that serves the same content as the primary site."
  };

  const forceWwwHelp =
    forceWww === "www"
      ? "Visitors are redirected to the www hostname."
      : forceWww === "non-www"
        ? "Visitors are redirected to the non-www hostname."
        : "No redirect preference is applied.";

  /* —— Add Domain —— */
  if (mode === "add") {
    return (
      <div className="rp-page">
        <div className="rp-page-head">
          <div>
            <h1 className="rp-page-title">Add Domain</h1>
            <p className="rp-page-sub">
              Add an addon domain, subdomain, or alias to a hosting account.
            </p>
          </div>
          <div className="rp-page-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setMode("list");
                setError("");
              }}
            >
              ← Domain List
            </button>
          </div>
        </div>

        {error ? <div className="error-banner">{error}</div> : null}

        <form onSubmit={(e) => void submitAdd(e)}>
          {/* 01 Domain */}
          <section className="rp-wiz-card">
            <h3>01 · Domain</h3>
            <p>Account, type and hostname for the new site.</p>

            {staff ? (
              <div className="field" style={{ marginBottom: 14 }}>
                <label>Hosting Account</label>
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  required
                >
                  <option value="">Select account…</option>
                  {endUsers.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.username}
                      {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                    </option>
                  ))}
                </select>
                {selectedAccount ? (
                  <p className="card-meta" style={{ marginTop: 6 }}>
                    Owner: {selectedAccount.ownerUsername || "root"} ·{" "}
                    {domainCountForUser} domain(s)
                    {selectedAccount.packageName
                      ? ` · ${selectedAccount.packageName}`
                      : ""}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 650,
                  color: "var(--muted)",
                  display: "block",
                  marginBottom: 8
                }}
              >
                Domain type
              </label>
              <div className="rp-tag-row">
                {(
                  [
                    ["addon", "Addon Domain"],
                    ["subdomain", "Subdomain"],
                    ["alias", "Alias"]
                  ] as const
                ).map(([k, lab]) => (
                  <button
                    key={k}
                    type="button"
                    className={`rp-tag${type === k ? " is-selected" : ""}`}
                    onClick={() => setType(k)}
                  >
                    {lab}
                    <small>{typeHelp[k]}</small>
                  </button>
                ))}
                {(!isEndUser ||
                  sites.every((s) => s.type !== "primary")) && (
                  <button
                    type="button"
                    className={`rp-tag${type === "primary" ? " is-selected" : ""}`}
                    onClick={() => setType("primary")}
                  >
                    Primary
                    <small>{typeHelp.primary}</small>
                  </button>
                )}
              </div>
            </div>

            <div className="field">
              <label>Domain</label>
              <input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                required
                placeholder="example.com"
                autoComplete="off"
              />
              {domain.trim() ? (
                <p
                  className="card-meta"
                  style={{
                    marginTop: 6,
                    color: domainValidation.ok
                      ? "var(--mint)"
                      : "var(--danger)"
                  }}
                >
                  {domainValidation.ok ? "✓ " : "✕ "}
                  {domainValidation.msg}
                </p>
              ) : null}
            </div>
          </section>

          {/* 02 Web config */}
          <section className="rp-wiz-card">
            <h3>02 · Web Configuration</h3>
            <p>Document root and redirect behavior.</p>

            <div className="field" style={{ marginBottom: 12 }}>
              <label>Domain path</label>
              <div className="dl-path-input">
                <span className="dl-path-prefix mono">{homePrefix}</span>
                <input
                  value={documentRoot}
                  onChange={(e) => {
                    setAutoPath(false);
                    setDocumentRoot(e.target.value);
                  }}
                  placeholder={
                    domain.trim()
                      ? `public_html/${domain.trim().toLowerCase()}`
                      : "public_html/example.com"
                  }
                  disabled={autoPath}
                />
              </div>
              <label className="rp-check" style={{ marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={autoPath}
                  onChange={(e) => setAutoPath(e.target.checked)}
                />
                <span>Auto-generate path from domain name</span>
              </label>
            </div>

            <div className="field">
              <label>Domain redirect</label>
              <select
                value={forceWww}
                onChange={(e) => setForceWww(e.target.value as ForceWww)}
              >
                <option value="none">No preference</option>
                <option value="www">Always use www</option>
                <option value="non-www">Always use non-www</option>
              </select>
              <p className="card-meta" style={{ marginTop: 6 }}>
                {forceWwwHelp}
              </p>
            </div>
          </section>

          {/* 03 Security & PHP */}
          <section className="rp-wiz-card">
            <h3>03 · Security &amp; PHP</h3>
            <p>SSL certificate and PHP environment for this domain.</p>

            <div
              className="field"
              style={{
                padding: 14,
                border: "1px solid var(--line)",
                borderRadius: 12,
                background: "var(--bg2)",
                marginBottom: 14
              }}
            >
              <label className="rp-check">
                <input
                  type="checkbox"
                  checked={issueSsl}
                  onChange={(e) => setIssueSsl(e.target.checked)}
                />
                <span>
                  Issue Let&apos;s Encrypt certificate automatically{" "}
                  <span className="rp-status rp-status-ok">Recommended</span>
                  <small>
                    RealPanel will request and configure a free certificate
                    after the domain is created.
                  </small>
                </span>
              </label>
            </div>

            <div className="field">
              <label>PHP version</label>
              <select
                value={phpVersion}
                onChange={(e) => setPhpVersion(e.target.value)}
              >
                {phpVersions.map((p) => (
                  <option key={p.version} value={p.version}>
                    PHP {p.version}
                    {p.default ? " · Default for this server" : ""}
                  </option>
                ))}
                <option value="static">Static only (no PHP)</option>
              </select>
              <p className="card-meta" style={{ marginTop: 6 }}>
                Change per-domain PHP later in MultiPHP Manager.
              </p>
            </div>

            <details style={{ marginTop: 14 }}>
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em"
                }}
              >
                Advanced Options
              </summary>
              <p className="card-meta" style={{ margin: "8px 0 10px" }}>
                Only change these if you understand DNS/web-server behavior.
              </p>
              <label className="rp-check">
                <input
                  type="checkbox"
                  checked={allowWildcard}
                  onChange={(e) => setAllowWildcard(e.target.checked)}
                />
                <span>
                  Wildcard mapping
                  <small>
                    Map *.domain.com to this folder (DNS must also point here).
                  </small>
                </span>
              </label>
            </details>
          </section>

          {/* Summary */}
          <section className="rp-wiz-card">
            <h3>Ready to create</h3>
            <dl className="rp-ready-dl">
              <div>
                <dt>Account</dt>
                <dd className="mono">
                  {selectedAccount?.username || currentUser.username}
                </dd>
              </div>
              <div>
                <dt>Domain</dt>
                <dd>{domain || "—"}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{typeLabel(type)}</dd>
              </div>
              <div>
                <dt>Path</dt>
                <dd className="mono" style={{ fontSize: 12 }}>
                  {homePrefix}
                  {documentRoot || "…"}
                </dd>
              </div>
              <div>
                <dt>PHP</dt>
                <dd>{phpVersion === "static" ? "Static" : `PHP ${phpVersion}`}</dd>
              </div>
              <div>
                <dt>SSL</dt>
                <dd>{issueSsl ? "Let's Encrypt" : "None"}</dd>
              </div>
            </dl>
          </section>

          <div className="rp-wiz-footer">
            <div className="rp-wiz-footer-meta">
              {gatewayPort ? `Preview gateway :${gatewayPort}` : ""}
            </div>
            <div className="rp-wiz-footer-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => setMode("list")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy || !domainValidation.ok}
              >
                {busy ? "Adding…" : "Add Domain"}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  /* —— Domain List —— */
  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">Domains</h1>
          <p className="rp-page-sub">
            All hosted domains across accounts
            {gatewayPort ? (
              <>
                {" "}
                · gateway <span className="mono">{gatewayPort}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="rp-page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefresh()}
            disabled={busy}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            onClick={() => {
              setMode("add");
              setError("");
              setMsg("");
            }}
          >
            + Add Domain
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="rp-summary-strip">
        <div className="rp-sum">
          <span className="rp-sum-label">Total Domains</span>
          <span className="rp-sum-value">{counts.total}</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Primary</span>
          <span className="rp-sum-value">{counts.primary}</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Addon</span>
          <span className="rp-sum-value">{counts.addon}</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Subdomain</span>
          <span className="rp-sum-value">{counts.sub}</span>
        </div>
      </div>

      <div className="rp-toolbar">
        <input
          className="rp-toolbar-search"
          placeholder="Search domain, user, path…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All types</option>
          <option value="primary">Primary</option>
          <option value="addon">Addon</option>
          <option value="subdomain">Subdomain</option>
          <option value="alias">Alias</option>
        </select>
        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value) || 50);
            setPage(1);
          }}
        >
          {[25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
        {selectedIds.length === 0 ? (
          <span className="rp-bulk-hint">
            Select domains to enable bulk actions
          </span>
        ) : (
          <div className="rp-bulk-bar">
            <button
              type="button"
              className="btn btn-danger btn-sm"
              disabled={busy}
              onClick={() => void deleteSelected()}
            >
              Delete Selected ({selectedIds.length})
            </button>
          </div>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={exportCsv}
        >
          Export CSV
        </button>
      </div>

      <div className="rp-panel">
        <table className="rp-acct-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={(e) => {
                    setSelected((prev) => {
                      const next = { ...prev };
                      for (const s of selectableOnPage) {
                        next[s.id] = e.target.checked;
                      }
                      return next;
                    });
                  }}
                  aria-label="Select page"
                />
              </th>
              <th>
                <button
                  type="button"
                  className="dl-sort"
                  onClick={() => {
                    if (sortKey === "domain")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("domain");
                      setSortDir("asc");
                    }
                  }}
                >
                  Domain{" "}
                  {sortKey === "domain" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                </button>
              </th>
              <th>Login</th>
              <th>
                <button
                  type="button"
                  className="dl-sort"
                  onClick={() => {
                    if (sortKey === "user")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("user");
                      setSortDir("asc");
                    }
                  }}
                >
                  User
                </button>
              </th>
              <th>Owner</th>
              <th>IP</th>
              <th>
                <button
                  type="button"
                  className="dl-sort"
                  onClick={() => {
                    if (sortKey === "path")
                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                    else {
                      setSortKey("path");
                      setSortDir("asc");
                    }
                  }}
                >
                  Path
                </button>
              </th>
              <th>Type</th>
              <th style={{ width: 44 }} />
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 40 }}>
                  <strong style={{ display: "block", marginBottom: 6 }}>
                    No domains found
                  </strong>
                  <span className="faint">
                    Adjust filters or add your first domain.
                  </span>
                </td>
              </tr>
            ) : (
              pageItems.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.type !== "primary" ? (
                      <input
                        type="checkbox"
                        checked={Boolean(selected[s.id])}
                        onChange={(e) =>
                          setSelected((p) => ({
                            ...p,
                            [s.id]: e.target.checked
                          }))
                        }
                      />
                    ) : null}
                  </td>
                  <td>
                    <a
                      className="dl-domain"
                      href={
                        s.sslEnabled
                          ? `https://${s.domain}`
                          : `http://${s.domain}`
                      }
                      target="_blank"
                      rel="noreferrer"
                    >
                      {s.domain}
                    </a>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="dl-login-btn"
                      title={
                        staff
                          ? `Log in as ${s.username || "user"}`
                          : `Open ${s.domain}`
                      }
                      disabled={busy}
                      onClick={() => void loginAsUser(s)}
                    >
                      <span className="dl-login-orb" aria-hidden />
                    </button>
                  </td>
                  <td className="mono">{s.username || "—"}</td>
                  <td className="faint">{ownerOf(s)}</td>
                  <td className="mono faint">{primaryIp || "—"}</td>
                  <td>
                    <span className="dl-path mono" title={pathDisplay(s)}>
                      {pathDisplay(s)}
                    </span>
                  </td>
                  <td>
                    <span className={`dl-type dl-type-${s.type}`}>
                      {typeLabel(s.type as SiteType)}
                    </span>
                  </td>
                  <td>
                    {s.type !== "primary" ? (
                      <button
                        type="button"
                        className="dl-trash"
                        title={`Delete ${s.domain}`}
                        disabled={busy}
                        onClick={() => void deleteOne(s)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7h12z"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderTop: "1px solid var(--line)",
              fontSize: 13,
              color: "var(--muted)"
            }}
          >
            <span>
              Page {safePage} of {totalPages} · {filtered.length} domains
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {pageItems.some((s) => s.status !== "active") ? (
        <div className="rp-panel" style={{ padding: 14 }}>
          <div className="rp-card-hd" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Disabled on this page</h3>
          </div>
          {pageItems
            .filter((s) => s.status !== "active")
            .map((s) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 0"
                }}
              >
                <span className="mono">{s.domain}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => void onEnable(s.id).then(() => onRefresh())}
                >
                  Enable
                </button>
              </div>
            ))}
        </div>
      ) : null}

      {/* keep ssl custom panel unused unless needed - renew helper */}
      {staff ? (
        <div className="rp-foot-meta">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={async () => {
              try {
                await onRenewSsl(true);
                setMsg("SSL renew dry-run completed.");
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              }
            }}
          >
            SSL dry-run
          </button>
        </div>
      ) : null}
    </div>
  );
}
